import { PaymentMethodConditions } from '@nemuru-eng/checkout-js';
import { LenderName, PercentDto } from '@nemuru-eng/js-api-client';

import { AbstractConditionsGroup } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/ConditionsGroup/AbstractConditionsGroup';
import {
  ConditionDto,
  SetupFeeConditionDto,
} from '@/components/modules/Application/components/PaymentMethod/_shared/types/ConditionDto';
import { AutoLoanConditions } from '@/components/modules/Application/components/PaymentMethod/AutoLoan/types/AutoLoanConditions';
import { ProviderName } from '@/types/ProviderName';

import { LoanConditions as LoanLoanConditions } from '../../types/LoanConditions';

type LoanConditions = LoanLoanConditions | AutoLoanConditions;

export class LoanConditionsGroup extends AbstractConditionsGroup {
  protected bestCandidateConditions: LoanConditions | null = null;
  public readonly conditions: LoanConditions[];

  constructor(conditions: LoanConditions[]) {
    super(conditions);
    this.conditions = conditions;
  }

  public static fromSimulatorConditions(conditions: PaymentMethodConditions): LoanConditionsGroup {
    const condition = conditions.conditions;

    if (!condition) throw new Error('No conditions available');

    return new LoanConditionsGroup([
      {
        apr: condition.apr,
        providerName: condition.lenderName,
        pricingId: condition.pricingId,
        principal: conditions.amount,
        annualInterest: condition.annualInterest,
        term: condition.term,
        setupFeeType: condition.setupFeeType,
        setupFee: condition.setupFeeAmount,
        gracePeriod: condition.gracePeriod,
        gracePercentage: condition.gracePercentage,
        instalmentTotalAmount: condition.instalmentTotalAmount,
        totalAmount: condition.totalAmount,
        merchantDiscountPercentage: new PercentDto(0.0),
        ...(condition.insurance && { insurance: condition.insurance }),
      },
    ]);
  }

  public getCandidateConditions(): LoanConditions {
    return super.getCandidateConditions() as LoanConditions;
  }

  protected getBestCandidateConditions(providers?: ProviderName[]): LoanConditions {
    const filteredConditions = providers
      ? this.conditions.filter((c) => c.providerName && providers.includes(c.providerName))
      : this.conditions;

    const bestCandidateConditions = filteredConditions.reduce((acc, condition) => {
      if (acc.apr.value > condition.apr.value) {
        return condition;
      }
      return acc;
    }, filteredConditions[0]);

    return bestCandidateConditions;
  }

  public providerNames(): LenderName[] {
    return [
      ...new Set(this.conditions.map((condition) => condition.providerName).filter((providerName) => !!providerName)),
    ];
  }

  public get annualInterest(): ConditionDto | null {
    const bestCandidateConditions = this.getCandidateConditions();
    if (bestCandidateConditions.annualInterest === undefined) {
      return null;
    }
    const hasMultiple = [...new Set(this.conditions.map((condition) => condition.annualInterest?.value))].length > 1;
    return {
      value: bestCandidateConditions.annualInterest.value,
      string: bestCandidateConditions.annualInterest.string,
      hasMultiple,
    };
  }

  public get apr(): ConditionDto {
    const bestCandidateConditions = this.getCandidateConditions();
    const hasMultiple = [...new Set(this.conditions.map((condition) => condition.apr.value))].length > 1;
    return {
      value: bestCandidateConditions.apr.value,
      string: bestCandidateConditions.apr.string,
      hasMultiple,
    };
  }

  public get setupFee(): SetupFeeConditionDto | null {
    const bestCandidateConditions = this.getCandidateConditions();
    if (bestCandidateConditions.setupFee === undefined || !bestCandidateConditions.setupFeeType) {
      return null;
    }
    const hasMultiple = [...new Set(this.conditions.map((condition) => condition.setupFee?.value))].length > 1;
    return {
      value: bestCandidateConditions.setupFee.value,
      string: bestCandidateConditions.setupFee.string,
      type: bestCandidateConditions.setupFeeType,
      hasMultiple,
    };
  }

  public get merchantDiscountPercentage(): ConditionDto | null {
    const bestCandidateConditions = this.getCandidateConditions();
    if (bestCandidateConditions.merchantDiscountPercentage === undefined) {
      return null;
    }
    const hasMultiple =
      [...new Set(this.conditions.map((condition) => condition.merchantDiscountPercentage?.value))].length > 1;
    return {
      value: bestCandidateConditions.merchantDiscountPercentage.value,
      string: bestCandidateConditions.merchantDiscountPercentage.string,
      hasMultiple,
    };
  }

  public get gracePeriod(): ConditionDto | null {
    const bestCandidateConditions = this.getCandidateConditions();
    if (bestCandidateConditions.gracePeriod === undefined) {
      return null;
    }
    const hasMultiple = [...new Set(this.conditions.map((condition) => condition.gracePeriod))].length > 1;
    return {
      value: bestCandidateConditions.gracePeriod,
      string: `${bestCandidateConditions.gracePeriod}`,
      hasMultiple,
    };
  }
}
